import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { ResponsivePicture } from '../..';
import {
	Dialog,
	DialogCloseButton,
} from '../../../features/shared/components/dialog';
import { Z_INDEX_DIALOG_TOP_CHATBOT } from '../../../features/shared/styles';
import { Color } from '../../../features/shared/styles/Color';
import { StyledDialogContent } from '../../../features/shared/components/dialog/DialogContent';
import {
	ORIENTATION,
	useDeviceOrientation,
} from '../../../features/shared/utils/useDeviceOrientation';
import { useTranslation } from '../../../features/shared/i18n/translate/useTranslation';
import Responsive from '../../utils/Responsive';

interface ImageDialogProps {
	imageSrc: string;
	alt?: string;
	setDialogOpen: (isDialogOpen: boolean) => void;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export default function ImageDialog({
	alt,
	imageSrc,
	setDialogOpen,
}: ImageDialogProps) {
	const orientation = useDeviceOrientation();
	const { t } = useTranslation();

	return (
		<StyledDialog
			ariaLabel={alt || t('expanded_image_view')}
			onClose={() => setDialogOpen(false)}
		>
			<StyledDialogCloseButton />
			<StyledResponsivePicture
				orientation={orientation}
				alt={alt}
				source={imageSrc}
			/>
		</StyledDialog>
	);
}

const StyledDialogCloseButton = styled(DialogCloseButton)`
	position: absolute;
	top: 24px;
	right: 24px;
	background-color: ${Color.BackgroundWhite};

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		background-color: ${Color.Surface};
	}
`;

const StyledDialog = styled(Dialog)`
	width: fit-content;
	border: none;
	padding: 0;
	height: auto;
	width: auto;
	z-index: ${Z_INDEX_DIALOG_TOP_CHATBOT};
	position: fixed;
	overflow: hidden;
	display: flex;
	justify-content: center;
	align-items: center;

	${StyledDialogContent} {
		border-radius: 16px;
		position: relative;

		&[data-reach-dialog-content] {
			width: 70%;
		}

		@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
			&[data-reach-dialog-content] {
				width: inherit;
			}
			border-radius: 0;
			display: flex;
			justify-content: center;
			align-items: center;
			max-width: unset;
		}
	}

	::backdrop {
		background: ${Color.DialogBackground};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		border-radius: 0;
		min-height: 100vh;
		min-width: 100vw;

		@supports (height: 100dvh) {
			min-height: 100dvh;
			min-width: 100dvw;
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		width: 100vw;
		::backdrop {
			background: ${Color.Neutral30};
		}
	}
`;

const StyledResponsivePicture = styled(ResponsivePicture)<{
	orientation: ORIENTATION;
}>`
	display: flex;

	border-radius: 16px;
	img {
		width: 100%;
		border-radius: 16px;
		object-fit: cover;
		aspect-ratio: 3/2;

		${({ orientation }) =>
			orientation === ORIENTATION.landscape
				? css`
						@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
							border-radius: 0 !important;
							width: 74%;
							margin: 0 auto;
						}
				  `
				: css`
						@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
							border-radius: 0;
							margin: 0;
							width: 100%;
						}
				  `}
	}
`;
